import { apiClient } from '@/lib/api.ts'

export type QuoteScenario = 'laptop' | 'teams' | 'meetingRoom' | 'free'
export type QuoteStatus = 'draft' | 'ready' | 'exported' | 'archived'
export type QuoteMessageRole = 'user' | 'assistant' | 'tool'
export type PositionKind = 'article' | 'service' | 'note' | 'subtotal'

export interface Quote {
  id: string
  tenantId: string
  createdByUserId: string
  customerSapBpCode: string | null
  customerTanssId: string | null
  customerName: string | null
  scenario: QuoteScenario
  status: QuoteStatus
  sapDocEntry: number | null
  sapDocNum: number | null
  title: string
  summaryMd: string | null
  modelId: string | null
  ticketId: string | null
  comments: string | null
  contactPersonCode: string | null
  contactPersonName: string | null
  sapExportHistory: string | null
  createdAt: string
  updatedAt: string
}

export interface SapExportHistoryEntry {
  docNum: number | null
  docEntry: number | null
  exportedAt: string
}

export interface QuoteMessage {
  id: string
  quoteId: string
  role: QuoteMessageRole
  content: string
  toolName: string | null
  toolInputJson: unknown | null
  toolOutputJson: unknown | null
  imagesJson: Array<{ mediaType: string; data: string }> | null
  createdByUserId: string | null
  createdByUserName: string | null
  createdAt: string
}

export interface QuotePosition {
  id: string
  quoteId: string
  sortIndex: number
  sapItemCode: string | null
  description: string
  /** Zeilenerweiterung (FreeText in SAP) — text shown below the article line. */
  lineText: string | null
  quantity: string | number
  unitPrice: string | number | null
  discountPct: string | number | null
  kind: PositionKind
  source: 'ai' | 'manual' | 'standard'
  standardId: string | null
  needsSapCreation: boolean
  isOptional: boolean
  parentPositionId: string | null
  purchasePrice: string | number | null
  createdAt: string
}

export interface SapContactPerson {
  CntctCode: number
  Name: string
  FirstName?: string
  LastName?: string
  E_Mail?: string
  Phone1?: string
  Position?: string
}

export async function fetchQuote(quoteId: string): Promise<Quote> {
  const { data } = await apiClient.get(`/quotes/${quoteId}`)
  return data.data as Quote
}

export interface QuoteCustomerPatch {
  customerSapBpCode?: string | null
  customerTanssId?: string | null
  customerName?: string | null
  tenantId?: string | null
  title?: string
  ticketId?: string | null
  comments?: string | null
  contactPersonCode?: string | null
  contactPersonName?: string | null
}

export async function updateQuote(
  quoteId: string,
  patch: QuoteCustomerPatch,
): Promise<Quote> {
  const { data } = await apiClient.patch(`/quotes/${quoteId}`, patch)
  return data.data as Quote
}

export async function copyQuote(
  quoteId: string,
  override?: QuoteCustomerPatch,
): Promise<Quote> {
  const { data } = await apiClient.post(`/quotes/${quoteId}/copy`, override ?? {})
  return data.data as Quote
}

export async function reopenQuote(quoteId: string): Promise<Quote> {
  const { data } = await apiClient.post(`/quotes/${quoteId}/reopen`, {})
  return data.data as Quote
}

function normalizeImagesJson(raw: unknown): Array<{ mediaType: string; data: string }> | null {
  if (Array.isArray(raw)) return raw as Array<{ mediaType: string; data: string }>
  if (typeof raw === 'string' && raw.length > 0) {
    try { return JSON.parse(raw) } catch { return null }
  }
  return null
}

export async function fetchQuoteMessages(quoteId: string): Promise<QuoteMessage[]> {
  const { data } = await apiClient.get(`/quotes/${quoteId}/messages`)
  const messages = data.data as QuoteMessage[]
  return messages.map((m) => ({ ...m, imagesJson: normalizeImagesJson(m.imagesJson) }))
}

export async function sendQuoteMessage(
  quoteId: string,
  content: string,
): Promise<{ messages: QuoteMessage[] }> {
  const { data } = await apiClient.post(`/quotes/${quoteId}/messages`, { content })
  return data.data as { messages: QuoteMessage[] }
}

// ─── Streaming chat (Server-Sent Events) ─────────────────────────────────────

export type ChatStreamEvent =
  | { type: 'user-message'; message: QuoteMessage }
  | { type: 'iteration-start'; iteration: number }
  | { type: 'text-delta'; text: string }
  /** Anthropic startet einen neuen Tool-Call-Block: Name + ID stehen schon
   *  fest, die Input-JSON kommt anschliessend stueckweise via tool-input-delta. */
  | { type: 'tool-use-start'; index: number; toolUseId: string; toolName: string }
  /** Haeppchen der Tool-Input-JSON. accumulatedJson kann noch unvollstaendig sein. */
  | { type: 'tool-input-delta'; index: number; partialJson: string; accumulatedJson: string }
  | { type: 'tool-use'; toolUseId: string; toolName: string; input: Record<string, unknown> }
  | { type: 'tool-result'; toolUseId: string; toolName: string; output: unknown; isError: boolean }
  | { type: 'assistant-persisted'; message: QuoteMessage }
  | { type: 'tool-persisted'; message: QuoteMessage }
  | { type: 'done' }
  | { type: 'error'; message: string }

/**
 * Sendet eine User-Message und konsumiert die SSE-Antwort token-fuer-token.
 * onEvent wird fuer jedes geparste Event aufgerufen. Liefert ein Promise,
 * das resolvet wenn der Server 'done' sendet oder die Verbindung schliesst.
 *
 * AbortController-signal kann genutzt werden, um den Stream vorzeitig
 * abzubrechen (Stop-Button).
 */
export async function streamQuoteMessage(
  quoteId: string,
  content: string,
  onEvent: (e: ChatStreamEvent) => void,
  signal?: AbortSignal,
  images?: Array<{ mediaType: string; data: string }>,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  // Auth + Tenant-Header analog zum apiClient setzen
  const authState = localStorage.getItem('auth-storage')
  if (authState) {
    try {
      const parsed = JSON.parse(authState) as { state?: { accessToken?: string } }
      const token = parsed.state?.accessToken
      if (token) headers['Authorization'] = `Bearer ${token}`
    } catch { /* ignore */ }
  }
  const tenantStored = localStorage.getItem('tenant-storage')
  if (tenantStored) {
    try {
      const parsed = JSON.parse(tenantStored) as { state?: { activeTenantId?: string } }
      const tenantId = parsed.state?.activeTenantId
      if (tenantId) headers['X-Tenant-Id'] = tenantId
    } catch { /* ignore */ }
  }

  const res = await fetch(`/api/quotes/${quoteId}/messages/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ content, ...(images && images.length > 0 && { images }) }),
    signal,
  })

  if (!res.ok || !res.body) {
    let detail = ''
    try {
      const j = await res.json()
      detail = (j as { error?: string }).error ?? ''
    } catch {
      detail = await res.text().catch(() => '')
    }
    throw new Error(detail || `Stream-Fehler: HTTP ${res.status}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let sepIdx: number
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx)
      buffer = buffer.slice(sepIdx + 2)
      // Heartbeat oder leere Events ignorieren
      const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data: '))
      if (!dataLine) continue
      const json = dataLine.slice(6).trim()
      if (!json) continue
      try {
        const raw = JSON.parse(json) as ChatStreamEvent
        const evt: ChatStreamEvent =
          raw.type === 'user-message' || raw.type === 'assistant-persisted' || raw.type === 'tool-persisted'
            ? { ...raw, message: { ...raw.message, imagesJson: normalizeImagesJson(raw.message.imagesJson) } }
            : raw
        onEvent(evt)
        if (evt.type === 'done' || evt.type === 'error') return
      } catch {
        /* malformed event, ignore */
      }
    }
  }
}

export async function fetchQuotePositions(quoteId: string): Promise<QuotePosition[]> {
  const { data } = await apiClient.get(`/quotes/${quoteId}/positions`)
  return data.data as QuotePosition[]
}

export async function createQuotePosition(
  quoteId: string,
  input: {
    description: string
    sapItemCode?: string | null
    quantity: number
    kind: PositionKind
    unitPrice?: number | null
    discountPct?: number
  },
): Promise<QuotePosition> {
  const { data } = await apiClient.post(`/quotes/${quoteId}/positions`, input)
  return data.data as QuotePosition
}

export async function updateQuotePosition(
  quoteId: string,
  positionId: string,
  patch: Record<string, unknown>,
): Promise<QuotePosition> {
  const { data } = await apiClient.patch(`/quotes/${quoteId}/positions/${positionId}`, patch)
  return data.data as QuotePosition
}

export async function deleteQuotePosition(quoteId: string, positionId: string): Promise<void> {
  await apiClient.delete(`/quotes/${quoteId}/positions/${positionId}`)
}

export interface ExportValidationError {
  field: string
  message: string
}

export interface ExportSapResult {
  docEntry: number
  docNum: number
  updated?: boolean
  tenant?: { id: string; code: string | null; name: string | null }
}

export class QuoteExportValidationError extends Error {
  constructor(public readonly errors: ExportValidationError[]) {
    super(`Export nicht möglich — ${errors.length} Validierungsfehler`)
    this.name = 'QuoteExportValidationError'
  }
}

export async function deleteQuote(quoteId: string): Promise<void> {
  await apiClient.delete(`/quotes/${quoteId}`)
}

export async function fetchContactPersons(cardCode: string): Promise<SapContactPerson[]> {
  const { data } = await apiClient.get(`/quotes/customers/${encodeURIComponent(cardCode)}/contact-persons`)
  return (data.data ?? []) as SapContactPerson[]
}

export interface ExportStepResult {
  step: string
  errors?: ExportValidationError[]
  error?: string
  code?: string | null
  body?: Record<string, unknown>
  cardCode?: string
  docEntry?: number
  docNum?: number
  ok?: boolean
  updated?: boolean
}

/** Step-by-step SAP export. Each call is a short-lived HTTP request. */
export async function exportQuoteStep(
  quoteId: string,
  step: 'validate' | 'build-body' | 'create' | 'save-back',
  payload?: Record<string, unknown>,
): Promise<ExportStepResult> {
  const { data } = await apiClient.post(`/quotes/${quoteId}/export-step?step=${step}`, payload ?? {})
  return data as ExportStepResult
}

export async function exportQuoteToSap(quoteId: string): Promise<ExportSapResult> {
  try {
    const { data } = await apiClient.post(`/quotes/${quoteId}/export-sap`, {})
    return data.data as ExportSapResult
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number; data?: { errors?: ExportValidationError[]; error?: string } } }
    if (axiosErr?.response?.status === 422 && axiosErr.response.data?.errors) {
      throw new QuoteExportValidationError(axiosErr.response.data.errors)
    }
    const message =
      axiosErr?.response?.data?.error ??
      (err instanceof Error ? err.message : 'Unbekannter Fehler beim SAP-Export')
    throw new Error(message)
  }
}
